/**
 * Sanitize user-provided SVG markup for safe use with dangerouslySetInnerHTML.
 * Strips scripts, event handlers, and dangerous URL schemes in href/xlink:href/src.
 */

import { isSafeUrl } from './safeUrl';

const EVENT_ATTR = /^on/i;
const DANGEROUS_TAGS = new Set([
  'script',
  'foreignobject',
  'iframe',
  'object',
  'embed',
  'link',
  'meta',
  'base',
]);

function isDangerousHref(value: string): boolean {
  const v = value.trim().toLowerCase();
  if (!v) return false;
  if (v.startsWith('javascript:') || v.startsWith('vbscript:')) return true;
  // Block all data: in SVG navigation/resource attrs (including data:image/svg+xml nesting).
  if (v.startsWith('data:')) return true;
  if (/^[a-z][a-z0-9+.-]*:/i.test(v) && !isSafeUrl(value)) return true;
  return false;
}

function sanitizeElement(el: Element): void {
  const tag = el.tagName.toLowerCase();
  if (DANGEROUS_TAGS.has(tag)) {
    el.remove();
    return;
  }

  // Copy attrs first — NamedNodeMap mutates while iterating.
  const attrs = Array.from(el.attributes);
  for (const attr of attrs) {
    const name = attr.name;
    const value = attr.value;

    if (EVENT_ATTR.test(name)) {
      el.removeAttribute(name);
      continue;
    }

    const nameLower = name.toLowerCase();
    if (
      nameLower === 'href' ||
      nameLower === 'xlink:href' ||
      nameLower === 'src' ||
      nameLower.endsWith(':href')
    ) {
      if (isDangerousHref(value)) {
        el.removeAttribute(name);
      }
    }
  }

  Array.from(el.children).forEach((child) => sanitizeElement(child));
}

/** Returns sanitized SVG HTML, or empty string if unusable. */
export function sanitizeSvg(raw: unknown): string {
  if (typeof raw !== 'string' || !raw.trim()) return '';

  if (typeof DOMParser === 'undefined') {
    // Non-browser fallback: strip obvious vectors with regex (best-effort).
    return raw
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
      .replace(/(href|xlink:href|src)\s*=\s*("|')\s*(javascript|data|vbscript):/gi, '$1=$2#');
  }

  try {
    const parser = new DOMParser();
    // Wrap so bare path fragments still parse.
    const doc = parser.parseFromString(
      raw.trim().startsWith('<svg') ? raw : `<svg xmlns="http://www.w3.org/2000/svg">${raw}</svg>`,
      'image/svg+xml',
    );

    const parseError = doc.querySelector('parsererror');
    if (parseError) return '';

    const root = doc.documentElement;
    if (!root || root.tagName.toLowerCase() !== 'svg') return '';

    sanitizeElement(root);
    return new XMLSerializer().serializeToString(root);
  } catch {
    return '';
  }
}
